// Returns to supplier (ADR-054): data access for Procurement → Supplier
// returns (PX-PUR-120) and the "Return to supplier" action of a received PO.
//
//   purchase_return_page(company, scope, term, after, limit)  keyset (created_at desc, id desc)
//   purchase_return_document(id)                              header + lines + shipments
//   purchase_return_candidates(order)                         what can still go back per PO line
//   purchase_order_returns(order)                             the order's returns
//   save_purchase_return / release / cancel / close
//   post_purchase_return_shipment / reverse_purchase_return_shipment

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import type { StatusTone } from "@/components/ui";
import { isDemoMode, supabase } from "@/lib/supabase";

// Same lists as @patronix/domain/purchase-returns (that module brings zod;
// the route config imports this file and stays in the entry chunk).
export const returnStatuses = ["draft", "released", "partially_shipped", "shipped", "closed", "cancelled"] as const;
export type ReturnStatus = (typeof returnStatuses)[number];
export const returnReasons = ["defective", "damaged", "wrong_item", "surplus", "other"] as const;
export type ReturnReason = (typeof returnReasons)[number];
export type ReturnScope = "open" | "all";
export const returnScopes: ReturnScope[] = ["open", "all"];
export const isReturnScope = (v: unknown): v is ReturnScope => v === "open" || v === "all";

export const returnStatusTone = {
  draft: "neutral",
  released: "info",
  partially_shipped: "warning",
  shipped: "success",
  closed: "neutral",
  cancelled: "danger",
} as const satisfies Record<ReturnStatus, StatusTone>;

export interface ReturnRow {
  id: string;
  number: string | null;
  status: ReturnStatus;
  order_id: string;
  order_number: string;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_rma: string | null;
  line_count: number;
  created_at: string;
  released_at: string | null;
}

export interface ReceivedLot {
  lot_id: string;
  lot_code: string;
  qty: string | number;
}

export interface ReturnLine {
  id: string;
  line_no: number;
  order_line_id: string;
  order_line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  serial_controlled: boolean;
  warehouse_id: string;
  warehouse_code: string;
  project_code: string | null;
  base_qty: string | number;
  shipped_base_qty: string | number;
  base_uom_code: string;
  base_uom_decimals: number;
  reason: ReturnReason;
  received_base_qty: string | number;
  returnable_base_qty: string | number;
  ncr_id: string | null;
  ncr_number: string | null;
  lot_id: string | null;
  lot_code: string | null;
  location_id: string | null;
  location_code: string | null;
  serial_numbers: string[] | null;
  received_lots: ReceivedLot[];
}

export interface ReturnShipment {
  id: string;
  number: string;
  delivery_note: string | null;
  note: string | null;
  reverses_shipment_id: string | null;
  reversed_by_id: string | null;
  posted_at: string;
  posted_by_name: string | null;
  line_count: number;
  lines: {
    line_no: number;
    item_code: string;
    qty: string | number;
    uom_code: string;
    location_code: string | null;
    lot_code: string | null;
    serial_count: number | null;
    serial_numbers: string[] | null;
  }[];
}

export interface ReturnDocument {
  id: string;
  number: string | null;
  status: ReturnStatus;
  company_id: string;
  order_id: string;
  order_number: string;
  order_date: string;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_rma: string | null;
  note: string | null;
  external_ref: string | null;
  revision: number;
  line_count: number;
  created_at: string;
  created_by_name: string | null;
  released_at: string | null;
  released_by_name: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  lines: ReturnLine[];
  shipments: ReturnShipment[];
}

export interface CandidateLine {
  order_line_id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  received_base_qty: string | number;
  returned_base_qty: string | number;
  returnable_base_qty: string | number;
}

export interface Candidates {
  order_id: string;
  order_number: string;
  company_id: string;
  status: string;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  lines: CandidateLine[];
}

export interface ReturnLineInput {
  order_line_id: string;
  qty: string;
  reason: ReturnReason;
}

export interface ShipmentLineInput {
  return_line_id: string;
  qty: string;
  location_id?: string;
  lot_id?: string;
  serials?: string[];
}

export interface ListParams {
  companyId: string;
  scope: ReturnScope;
  q: string;
}

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export const purchaseReturnKeys = {
  all: ["procurement", "returns"] as const,
  list: (p: ListParams) => [...purchaseReturnKeys.all, "list", p] as const,
  detail: (id: string) => [...purchaseReturnKeys.all, "detail", id] as const,
  candidates: (orderId: string) => [...purchaseReturnKeys.all, "candidates", orderId] as const,
  ofOrder: (orderId: string) => [...purchaseReturnKeys.all, "of-order", orderId] as const,
};

export async function listPurchaseReturns(p: ListParams, cursor: string | null, signal: AbortSignal, size = 50): Promise<Page<ReturnRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("purchase_return_page", {
      p_company_id: p.companyId,
      p_scope: p.scope,
      p_term: p.q.trim() || null,
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as ReturnRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify({ c: last.created_at, id: last.id }) : null };
}

export function purchaseReturnListQuery(p: ListParams) {
  return infiniteQueryOptions({
    queryKey: purchaseReturnKeys.list(p),
    queryFn: ({ pageParam, signal }) => listPurchaseReturns(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<ReturnRow>) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

export function purchaseReturnDetailQuery(id: string) {
  return queryOptions({
    queryKey: purchaseReturnKeys.detail(id),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("purchase_return_document", { p_id: id }).abortSignal(signal);
      if (error) throw error;
      return (data ?? null) as ReturnDocument | null;
    },
    staleTime: 30_000,
    enabled: !!id && !isDemoMode,
  });
}

export function candidatesQuery(orderId: string) {
  return queryOptions({
    queryKey: purchaseReturnKeys.candidates(orderId),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("purchase_return_candidates", { p_order_id: orderId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? null) as Candidates | null;
    },
    staleTime: 10_000,
    enabled: !!orderId && !isDemoMode,
  });
}

export function orderReturnsQuery(orderId: string) {
  return queryOptions({
    queryKey: purchaseReturnKeys.ofOrder(orderId),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("purchase_order_returns", { p_order_id: orderId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? []) as { id: string; number: string | null; status: ReturnStatus; created_at: string }[];
    },
    staleTime: 30_000,
    enabled: !!orderId && !isDemoMode,
  });
}

export async function savePurchaseReturn(
  id: string | null,
  companyId: string,
  orderId: string | null,
  header: { revision?: number; supplier_rma: string | null; note: string | null },
  lines: ReturnLineInput[],
) {
  const { data, error } = await db().rpc("save_purchase_return", {
    p_id: id,
    p_company_id: companyId,
    p_order_id: orderId,
    p_header: header,
    p_lines: lines,
  });
  if (error) throw error;
  return data as { id: string; status: ReturnStatus; revision: number };
}

export type ReturnTransition = "release" | "cancel" | "close";

export async function transitionPurchaseReturn(id: string, action: ReturnTransition, revision: number) {
  const fn = { release: "release_purchase_return", cancel: "cancel_purchase_return", close: "close_purchase_return" }[action];
  const { error } = await db().rpc(fn, { p_id: id, p_revision: revision });
  if (error) throw error;
}

export async function postReturnShipment(returnId: string, deliveryNote: string | null, lines: ShipmentLineInput[]) {
  const { data, error } = await db().rpc("post_purchase_return_shipment", {
    p_return_id: returnId,
    p_delivery_note: deliveryNote,
    p_lines: lines,
  });
  if (error) throw error;
  return data as { id: string; number: string; return_status: ReturnStatus };
}

export async function reverseReturnShipment(id: string) {
  const { error } = await db().rpc("reverse_purchase_return_shipment", { p_id: id });
  if (error) throw error;
}

// Known database messages → i18n; the rest is shown as it comes.
const rules: [RegExp, string][] = [
  [/changed by someone else/, "prt.err.stale"],
  [/only on received orders/, "prt.err.notReceived"],
  [/shipped on this return; close it/, "prt.err.closeInstead"],
  [/already reversed/, "prt.err.reversed"],
  [/from a nonconformance report is not shipped/, "prt.err.ncrOpen"],
];
const lineRules: [RegExp, string][] = [
  [/^only .* can be returned/, "prt.err.over"],
  [/^lot is required/, "prt.err.lotRequired"],
  [/^lot was not received/, "prt.err.lotNotReceived"],
  [/^serial .* was not received/, "prt.err.serialNotReceived"],
  [/^the order line appears twice/, "prt.err.twice"],
  [/^more than the .* released/, "prt.err.overReleased"],
];

export function purchaseReturnErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^(?:Return )?[Ll]ine (\d+):? (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("po.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
